import React from 'react';
import { Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { normalizeStoredFormSchema } from './form-io.util';
import { FORMS_TABLE_ACTION_LABELS } from '../constants/forms.constants';
import EditIcon from '@mui/icons-material/Edit';
import MarkChatReadIcon from '@mui/icons-material/MarkChatRead';
import PreviewIcon from '@mui/icons-material/Preview';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import VisibilityIcon from '@mui/icons-material/Visibility';

export const FORMS_PAGE_SIZE = 10;

export const FORMS_COLUMNS = [
  {
    name: 'name',
    displayName: 'Name',
    sortOrder: 'none',
    isFilterable: true,
    isSortable: true,
    isVisible: true,
    type: 'value',
  },
  {
    name: 'createdBy',
    displayName: 'Created by',
    sortOrder: 'none',
    isFilterable: true,
    isSortable: true,
    isVisible: true,
    type: 'value',
  },
  {
    // Key stays `active`: filter and sort read the `Active` / `Inactive` label from `rowData.active`.
    name: 'active',
    displayName: 'Status',
    sortOrder: 'none',
    isFilterable: true,
    isSortable: true,
    isVisible: true,
    type: 'preDefined',
    preset: 'IconAndTextCell',
  },
  {
    name: 'submissionCount',
    displayName: 'Submission count',
    sortOrder: 'none',
    isFilterable: true,
    isSortable: true,
    isVisible: true,
    type: 'value',
  },
];

const NAVY_ICON_SX = { color: 'var(--navy-main)', fontSize: 24 };
const NAVY_ACTION_STYLE = { color: 'var(--navy-main)' };

export const FORM_STATUS_LABELS = Object.freeze({
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
});

/** Both statuses use the Feeds History Active icon. Inactive uses the Locked gray. */
const FORM_STATUS_VISUALS = Object.freeze({
  [FORM_STATUS_LABELS.ACTIVE]: { Icon: MarkChatReadIcon, color: 'var(--green-main)' },
  [FORM_STATUS_LABELS.INACTIVE]: { Icon: MarkChatReadIcon, color: 'var(--grey-100)' },
});

/**
 * @param {boolean | undefined} active
 * @returns {string} `Active` or `Inactive`
 */
export const formatFormStatusLabel = (active) =>
  active ? FORM_STATUS_LABELS.ACTIVE : FORM_STATUS_LABELS.INACTIVE;

/**
 * `IconAndTextCell` props for the All Forms Status column.
 * @param {boolean | undefined} active
 * @returns {{ icon: React.ReactNode, text: React.ReactNode }}
 */
export const buildFormStatusCellProps = (active) => {
  const label = formatFormStatusLabel(active);
  const { Icon, color } = FORM_STATUS_VISUALS[label];
  return {
    icon: <Icon sx={{ color }} data-testid={`form-status-icon-${label.toLowerCase()}`} />,
    text: <Typography sx={{ color, fontWeight: 'bold' }}>{label}</Typography>,
  };
};

const getCreatedByFullName = (createdBy) => {
  const first = (createdBy?.firstName ?? '').trim();
  const last = (createdBy?.lastName ?? '').trim();
  return `${first} ${last}`.trim();
};

const getCreatedByUsername = (createdBy) => {
  const username = createdBy?.username;
  if (username == null) {
    return '';
  }
  return String(username).trim();
};

const formatCreatedByDisplay = (form) => {
  const cb = form?.createdBy;
  if (cb == null) {
    return '';
  }
  return getCreatedByFullName(cb) || getCreatedByUsername(cb) || '';
};

const buildDisableAction = (form, onToggleActive, labels) => ({
  title: labels.DISABLE,
  onClick: () => onToggleActive(form),
  id: 'toggle-active',
  icon: <ToggleOffIcon sx={NAVY_ICON_SX} />,
  style: NAVY_ACTION_STYLE,
});

const buildEnableAction = (form, onToggleActive, labels) => ({
  title: labels.ENABLE,
  onClick: () => onToggleActive(form),
  id: 'toggle-active',
  icon: <ToggleOnIcon sx={NAVY_ICON_SX} />,
  style: NAVY_ACTION_STYLE,
});

const buildViewSubmissionsAction = (form, onViewSubmissions) => ({
  title: 'View Submissions',
  onClick: () => onViewSubmissions(form),
  id: 'view-submissions',
  icon: <VisibilityIcon sx={NAVY_ICON_SX} />,
  style: NAVY_ACTION_STYLE,
});

/**
 * Action builders default to All Forms table copy; pass another label set (e.g.
 * `FORM_SUBMISSIONS_ACTION_LABELS`) to reuse the same handler / icon / id elsewhere.
 */
export const buildPreviewFormAction = (form, onPreviewForm, labels = FORMS_TABLE_ACTION_LABELS) => ({
  title: labels.PREVIEW,
  onClick: () => onPreviewForm(form),
  id: 'preview-form',
  icon: <PreviewIcon sx={NAVY_ICON_SX} />,
  style: NAVY_ACTION_STYLE,
});

export const buildEditAction = (form, onEditForm, labels = FORMS_TABLE_ACTION_LABELS) => ({
  title: labels.EDIT,
  onClick: () => onEditForm(form),
  id: 'edit-form',
  icon: <EditIcon sx={NAVY_ICON_SX} />,
  style: NAVY_ACTION_STYLE,
});

export const buildDuplicateFormAction = (form, onDuplicateForm) => ({
  title: 'Duplicate',
  onClick: () => onDuplicateForm(form),
  id: 'duplicate-form',
  icon: <ContentCopyIcon sx={NAVY_ICON_SX} />,
  style: NAVY_ACTION_STYLE,
});

const buildOptionalAction = (form, handler, actionBuilder) =>
  typeof handler === 'function' ? [actionBuilder(form, handler)] : [];

/**
 * Enable / Disable toggle. `form_manage` holders act on any form, so there is no creator check;
 * the action is omitted entirely when no handler is supplied.
 */
export const buildToggleAction = (form, onToggleActive, labels = FORMS_TABLE_ACTION_LABELS) => {
  if (typeof onToggleActive !== 'function') {
    return null;
  }
  if (!form.active) {
    return buildEnableAction(form, onToggleActive, labels);
  }
  return buildDisableAction(form, onToggleActive, labels);
};

/**
 * @param {object} form
 * @param {object} handlers
 * @param {function(object): void} [handlers.onToggleActive]
 * @param {function(object): void} [handlers.onPreviewForm]
 * @param {function(object): void} [handlers.onViewSubmissions]
 * @param {function(object): void} [handlers.onEditForm]
 * @param {function(object): void} [handlers.onDuplicateForm]
 */
const getFormRowActions = (
  form,
  { onToggleActive, onPreviewForm, onViewSubmissions, onEditForm, onDuplicateForm },
) => [
  ...buildOptionalAction(form, onViewSubmissions, buildViewSubmissionsAction),
  ...buildOptionalAction(form, onPreviewForm, buildPreviewFormAction),
  ...buildOptionalAction(form, onEditForm, buildEditAction),
  ...buildOptionalAction(form, onDuplicateForm, buildDuplicateFormAction),
  ...buildOptionalAction(form, onToggleActive, buildToggleAction),
];

/**
 * @param {object[]} forms
 * @param {object} actionHandlers
 * @param {function(object): void} [actionHandlers.onToggleActive]
 * @param {function(object): void} [actionHandlers.onPreviewForm]
 * @param {function(object): void} [actionHandlers.onViewSubmissions]
 * @param {function(object): void} [actionHandlers.onEditForm]
 * @param {function(object): void} [actionHandlers.onDuplicateForm]
 */
export const mapFormsToGridData = (forms, actionHandlers) =>
  forms.map((form) => ({
    isSelected: false,
    rowData: {
      name: form.name,
      createdBy: formatCreatedByDisplay(form),
      active: formatFormStatusLabel(form.active),
      submissionCount: String(form.submissionCount ?? 0),
    },
    preDefineComponentProps: {
      active: buildFormStatusCellProps(form.active),
      actions: getFormRowActions(form, actionHandlers),
    },
  }));

const toFormRow = (f) => ({
  id: f.id,
  name: f.name ?? '',
  active: Boolean(f.active),
  submissionCount: typeof f.submissionCount === 'number' ? f.submissionCount : 0,
  personKey: f.personKey,
  createdBy: f.createdBy ?? null,
  schema: f.schema,
});

/** Shape API `/api/content/form` rows for the Forms DataGrid. */
export const mapApiRowsToForms = (rows) => {
  if (!Array.isArray(rows)) {
    return [];
  }
  return rows.map(toFormRow);
};

/** Map GET `/api/content/form/:id` JSON for row actions / previews (same shape as list rows). */
export const mapApiFormDefinitionToForm = (f) => {
  if (f == null) {
    return null;
  }
  return toFormRow(f);
};

const toFormsCsvRow = (form) => [
  form.name ?? '',
  formatFormStatusLabel(form.active),
  typeof form.submissionCount === 'number' ? String(form.submissionCount) : '0',
];

export const FORMS_CSV_HEADERS = Object.freeze(['Name', 'Status', 'Submission count']);

export const FORMS_CSV_FILENAME = 'forms.csv';

/**
 * Build a CSV export payload (`filename`, `headers`, `rows`) for the Forms grid.
 * @param {readonly { name?: string, active?: boolean, submissionCount?: number }[]} forms
 */
export const buildFormsCsvExportPayload = (forms) => ({
  filename: FORMS_CSV_FILENAME,
  headers: [...FORMS_CSV_HEADERS],
  rows: forms.map(toFormsCsvRow),
});

const DUPLICATE_NAME_PREFIX = 'Copy of ';

/**
 * Builds the display name for a duplicated form (`Copy of {original}`).
 * @param {unknown} sourceName
 * @returns {string}
 */
export const buildDuplicatedFormDisplayName = (sourceName) => {
  const base =
    sourceName != null && String(sourceName).trim() !== ''
      ? String(sourceName).trim()
      : 'Untitled form';
  return `${DUPLICATE_NAME_PREFIX}${base}`;
};

/**
 * Payload for {@link createNewForm} from a GET `/api/content/form/:id` definition.
 * Submissions are not part of the definition and are never copied.
 *
 * @param {{ name?: string, active?: boolean, schema?: unknown } | null | undefined} definition
 * @returns {{ name: string, isActive: boolean, schema: object }}
 */
export const buildDuplicateFormCreatePayload = (definition) => ({
  name: buildDuplicatedFormDisplayName(definition?.name),
  isActive: definition?.active !== false,
  schema: normalizeStoredFormSchema(definition?.schema),
});
